<div class="dash-shell ops-dashboard">
  <!-- Hero -->
  <header class="dash-hero">
    <div class="dash-hero__body">
      <p class="dash-hero__eyebrow">OPS</p>
      <h1 class="dash-hero__title">Review queue</h1>
      <p class="dash-hero__sub">{{ heroSub() }}</p>
    </div>
  </header>

  @if (isLoading()) {
    <!-- Loading skeletons -->
    <div class="dash-kpi-grid">
      @for (i of [1, 2, 3, 4]; track i) {
        <div class="dash-skeleton" style="min-height: 160px"></div>
      }
    </div>
    <div class="dash-grid-2">
      <div class="dash-skeleton" style="min-height: 320px"></div>
      <div class="dash-skeleton" style="min-height: 320px"></div>
    </div>
  } @else if (loadError()) {
    <div class="dash-panel">
      <div class="dash-empty">
        <span class="dash-empty__icon"><app-icon name="alert-triangle" [size]="32" /></span>
        <p class="dash-empty__title">Couldn't load the queue</p>
        <p class="dash-empty__body">
          Something went wrong on our side.
        </p>
      </div>
    </div>
  } @else {
    <!-- Queue tiles, grouped by product -->
    <section class="ops-queue-groups" aria-label="Queues">
      @for (group of queueGroups(); track group.product) {
        <div class="ops-queue-group">
          <p class="ops-queue-group__product" [attr.data-product]="group.product">
            {{ group.productLabel }}
          </p>
          <div class="ops-queue-group__tiles">
            @for (tile of group.tiles; track tile.key; let i = $index) {
              <a
                class="ops-queue-tile ops-queue-tile--{{ tile.modifier }} dash-enter"
                [class.ops-queue-tile--urgent]="tile.queue.breaching > 0"
                [style.--dash-index]="i"
                [routerLink]="tile.queue.url"
                [attr.aria-label]="
                  group.productLabel + ' ' + tile.label + ': ' + tile.queue.waiting + ' waiting'
                "
              >
                <div class="ops-queue-tile__top">
                  <span class="ops-queue-tile__icon"
                    ><app-icon [name]="tile.icon" [size]="22"
                  /></span>
                  @if (tile.queue.breaching > 0) {
                    <span class="ops-queue-tile__sla ops-queue-tile__sla--breach">
                      <app-icon name="alert-triangle" [size]="12" />
                      {{ tile.queue.breaching }} breaching
                    </span>
                  } @else if (tile.hasSla && tile.queue.waiting > 0) {
                    <span class="ops-queue-tile__sla ops-queue-tile__sla--ok">On track</span>
                  }
                </div>
                <p class="ops-queue-tile__count">{{ tile.queue.waiting }}</p>
                <p class="ops-queue-tile__label">{{ tile.label }}</p>
                <p class="ops-queue-tile__sub">{{ tile.sub }}</p>
                <span class="ops-queue-tile__cta">
                  Go to queue <app-icon name="arrow-right" [size]="14" />
                </span>
              </a>
            }
          </div>
        </div>
      }

      <!-- SLA headline tile — links through to who's breaching -->
      <div class="ops-queue-group ops-queue-group--sla">
        <p class="ops-queue-group__product ops-queue-group__product--sla">SLA</p>
        @if (totalBreaching() > 0) {
          <a
            class="dash-kpi dash-kpi--link dash-kpi--alert dash-enter"
            routerLink="/ops/breaching"
            aria-label="View all items breaching SLA"
          >
            <div class="dash-kpi__top">
              <span class="dash-kpi__icon dash-kpi__icon--danger">
                <app-icon name="alert-triangle" [size]="18" />
              </span>
              <app-icon name="arrow-right" [size]="14" class="dash-kpi__go" />
            </div>
            <div>
              <p class="dash-kpi__value">{{ totalBreaching() }}</p>
              <p class="dash-kpi__label">Breaching SLA</p>
            </div>
          </a>
        } @else {
          <div class="dash-kpi dash-kpi--ok dash-enter">
            <div class="dash-kpi__top">
              <span class="dash-kpi__icon dash-kpi__icon--success">
                <app-icon name="check" [size]="18" />
              </span>
            </div>
            <div>
              <p class="dash-kpi__value">{{ totalBreaching() }}</p>
              <p class="dash-kpi__label">On track</p>
            </div>
          </div>
        }
      </div>
    </section>

    <!-- ① PIPELINE STRIP -->
    @if (pipelineStages().length > 0) {
      <section class="ops-pipeline" aria-label="Application pipeline">
        <div class="ops-pipeline__header">
          <p class="ops-pipeline__title">Application pipeline</p>
          <span class="ops-pipeline__total">{{ pipelineTotal() }} total</span>
        </div>
        <div class="ops-pipeline__stages" role="list">
          @for (stage of pipelineStages(); track stage.key; let last = $last) {
            <button
              class="ops-pipeline__segment {{ actorClass(stage.actor) }}"
              type="button"
              role="listitem"
              [attr.aria-label]="stage.label + ': ' + stage.count + ' items — click to filter worklist'"
              (click)="setLens('all', stage.key)"
            >
              <span class="ops-pipeline__seg-count">{{ stage.count }}</span>
              <span class="ops-pipeline__seg-label">{{ stage.label }}</span>
            </button>
            @if (!last) {
              <span class="ops-pipeline__arrow" aria-hidden="true">
                <app-icon name="arrow-right" [size]="14" />
              </span>
            }
          }
        </div>

        <!-- Slowest-stage callout -->
        @if (slowestStage(); as slow) {
          <p class="ops-pipeline__slowest">
            <app-icon name="clock" [size]="13" aria-hidden="true" />
            Slowest stage: {{ slow.label }} — {{ hoursInStageLabel(slow.hours) }}
          </p>
        }
      </section>
    }

    <!-- Intake — full-width hero chart -->
    <section aria-label="Applications over time">
      <div class="dash-panel">
        <div class="dash-panel__header">
          <div>
            <p class="dash-panel__title">Applications coming in</p>
            <p class="dash-panel__subtitle">
              Daily intake by product · last {{ summary()?.window_days }} days
            </p>
          </div>
          <span class="dash-panel__icon"><app-icon name="trending-up" [size]="18" /></span>
        </div>
        <div class="dash-panel__body">
          <app-chart [option]="intakeChartOption()" height="260px" />
        </div>
      </div>
    </section>

    <!-- Breakdown donuts: AI mix + application stages -->
    <section class="dash-grid-even" aria-label="Queue insight">
      <!-- ⑥ Renamed to "AI advisory" — it never decides (N6 gate) -->
      <div class="dash-panel">
        <div class="dash-panel__header">
          <div>
            <p class="dash-panel__title">AI advisory</p>
            <p class="dash-panel__subtitle">What the AI suggests across items in review</p>
          </div>
          <span class="dash-panel__icon"><app-icon name="sparkles" [size]="18" /></span>
        </div>
        <div class="dash-panel__body">
          <app-chart [option]="aiChartOption()" height="260px" />
        </div>
      </div>

      <div class="dash-panel">
        <div class="dash-panel__header">
          <div>
            <p class="dash-panel__title">Application stages</p>
            <p class="dash-panel__subtitle">Combined Kickstart Cash + Debtors Finance stage distribution</p>
          </div>
          <span class="dash-panel__icon"><app-icon name="chart" [size]="18" /></span>
        </div>
        <div class="dash-panel__body">
          <app-chart [option]="statusChartOption()" height="260px" />
        </div>
      </div>
    </section>

    <!-- Pre-applications — where a business starts. No amount and no SLA (ruling Q4). -->
    <section aria-label="Pre-applications waiting">
      <div class="dash-panel">
        <div class="dash-panel__header">
          <div>
            <p class="dash-panel__title">Pre-applications waiting</p>
            <p class="dash-panel__subtitle">Businesses waiting for a decision, longest wait first</p>
          </div>
          @if (preApplicationsWaitingCount() > 0) {
            <span class="ops-pre-count">{{ preApplicationsWaitingCount() }}</span>
          }
        </div>

        @if (preApplicationsWaiting().length === 0) {
          <div class="dash-empty">
            <span class="dash-empty__icon dash-empty__icon--ok">
              <app-icon name="check" [size]="32" />
            </span>
            <p class="dash-empty__title">No pre-applications are waiting</p>
            <p class="dash-empty__body">New submissions will appear here for a decision.</p>
          </div>
        } @else {
          <div class="ops-recent-wrap">
            <table class="ops-recent">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Submitted by</th>
                  <th>Recommendation</th>
                  <th>Dealt with by</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                @for (pre of preApplicationsWaiting(); track pre.short_id) {
                  <tr
                    class="ops-recent__row"
                    [routerLink]="pre.url"
                    [attr.aria-label]="'Open pre-application for ' + pre.entity_name"
                  >
                    <td>{{ pre.entity_name }}</td>
                    <td>{{ pre.submitted_by | dash }}</td>
                    <td>{{ preRecommendationLabel(pre) }}</td>
                    <td>{{ pre.assignee || 'Nobody' }}</td>
                    <td class="ops-recent__muted">
                      {{ pre.submitted_at | date: 'mediumDate' | dash }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
        <a class="ops-see-all" routerLink="/ops/pre-applications">
          See all pre-applications <app-icon name="arrow-right" [size]="14" />
        </a>
      </div>
    </section>

    <!-- ④ NEXT ACTIONS — primary worklist (replaces "Recent applications") -->
    <section #worklistSection aria-label="Next actions">
      <div class="dash-panel">
        <div class="dash-panel__header">
          <div>
            <p class="dash-panel__title">Next actions</p>
            <p class="dash-panel__subtitle">What needs you, most blocking first</p>
          </div>
          <!-- ② Lens segmented control -->
          <div class="ops-lens" role="group" aria-label="Filter worklist">
            <button
              class="ops-lens__btn"
              type="button"
              [class.ops-lens__btn--active]="lens() === 'mine'"
              (click)="lens.set('mine')"
            >
              Mine ({{ mineCount() }})
            </button>
            <button
              class="ops-lens__btn"
              type="button"
              [class.ops-lens__btn--active]="lens() === 'unassigned'"
              (click)="lens.set('unassigned')"
            >
              Unassigned ({{ unassignedCount() }})
            </button>
            <button
              class="ops-lens__btn"
              type="button"
              [class.ops-lens__btn--active]="lens() === 'all'"
              (click)="lens.set('all')"
            >
              All ({{ pipelineTotal() }})
            </button>
          </div>
        </div>

        @if (filteredWorklist().length === 0) {
          <div class="dash-empty">
            <span class="dash-empty__icon dash-empty__icon--ok">
              <app-icon name="check" [size]="32" />
            </span>
            <p class="dash-empty__title">Nothing needs you right now</p>
            <p class="dash-empty__body">
              Every submitted application has an owner and is moving — check back later or switch
              to "All" to see the full queue.
            </p>
          </div>
        } @else {
          <div class="ops-worklist-wrap">
            <table class="ops-worklist" aria-label="Next actions worklist">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Business</th>
                  <th class="ops-worklist__num">Amount</th>
                  <th>Review stage</th>
                  <th>Next step</th>
                  <th>Assignee</th>
                  <th>Age</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                @for (item of filteredWorklist(); track item.short_id) {
                  <tr
                    class="ops-worklist__row"
                    [routerLink]="item.url"
                    [attr.aria-label]="'Open ' + item.product + ' application ' + item.short_id"
                  >
                    <td>
                      <span class="ops-work-item__product" [attr.data-product]="item.product">{{
                        item.product
                      }}</span>
                    </td>
                    <td class="ops-worklist__entity">{{ item.entity_name }}</td>
                    <td class="ops-worklist__num">{{ item.amount | zar }}</td>
                    <td>
                      <!-- ③ CC chip + above-limit hint -->
                      <span class="ops-stage-chip ops-stage-chip--{{ item.review_stage.toLowerCase().replace('_', '-') }}">
                        {{ item.review_stage_label }}
                      </span>
                      @if (item.requires_cc) {
                        <span class="ops-cc-chip" aria-label="Requires Credit Committee">CC</span>
                      }
                    </td>
                    <td>
                      <span class="ops-next-step ops-next-step--{{ item.next_actor.toLowerCase().replace('_', '-') }}">
                        {{ item.next_step_label }}
                      </span>
                      <!-- ③ Above-limit hint for non-CC staff -->
                      @if (isAboveLimit(item.requires_cc)) {
                        <span class="ops-above-limit" aria-label="Above your authority limit">
                          above your limit — refer
                        </span>
                      }
                    </td>
                    <td>
                      @if (item.assignee) {
                        <span
                          class="ops-assignee"
                          [class.ops-assignee--me]="item.assignee_is_me"
                          [attr.title]="item.assignee"
                          [attr.aria-label]="'Assigned to ' + item.assignee"
                        >
                          {{ initials(item.assignee) }}
                        </span>
                      } @else {
                        <span class="ops-unassigned-pill">Unassigned</span>
                      }
                    </td>
                    <!-- ⑤ Age in current stage -->
                    <td class="ops-worklist__age" [attr.aria-label]="'In this stage for ' + hoursInStageLabel(item.hours_in_stage)">
                      {{ hoursInStageLabel(item.hours_in_stage) }}
                    </td>
                    <!-- ② Pick-up action — stop propagation so row link doesn't fire -->
                    <td class="ops-worklist__action" (click)="$event.stopPropagation()">
                      @if (item.can_pick_up) {
                        <button
                          class="ops-pickup-btn"
                          type="button"
                          [disabled]="pickingUp() === item.short_id"
                          [attr.aria-label]="'Pick up ' + item.short_id"
                          (click)="pickUp(item)"
                        >
                          {{ pickingUp() === item.short_id ? 'Picking up…' : 'Pick up' }}
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
        <!-- FLOW-346: the worklist is a summary of twelve; the full list is here. -->
        <a class="ops-see-all" routerLink="/ops/applications">
          See all applications <app-icon name="arrow-right" [size]="14" />
        </a>
      </div>
    </section>

    <!-- ⑤ OVERDUE — act first (with stage-age + slowest-stage callout) -->
    <section aria-label="Overdue items">
      <div class="dash-panel">
        <div class="dash-panel__header">
          <div>
            <p class="dash-panel__title">Overdue — act first</p>
            <p class="dash-panel__subtitle">Items past their SLA deadline, most overdue first</p>
          </div>
          @if (overdue().length > 0) {
            <span class="ops-overdue-count">{{ overdue().length }}</span>
          }
        </div>

        @if (overdue().length === 0) {
          <div class="dash-empty">
            <span class="dash-empty__icon dash-empty__icon--ok"
              ><app-icon name="check" [size]="32"
            /></span>
            <p class="dash-empty__title">Nothing is breaching SLA</p>
            <p class="dash-empty__body">Work through the queues in order — you're on top of it.</p>
          </div>
        } @else {
          <ul class="ops-overdue-list">
            @for (item of overdue(); track item.short_id) {
              <li>
                <a class="ops-work-item ops-work-item--overdue" [routerLink]="item.url">
                  <span class="ops-work-item__product" [attr.data-product]="item.product">{{
                    item.product
                  }}</span>
                  <span class="ops-work-item__ref">{{ item.short_id }}</span>
                  <span class="ops-work-item__main">
                    <span class="ops-work-item__entity">{{ item.entity_name }}</span>
                    <span class="ops-work-item__amount">{{ item.amount | zar }}</span>
                  </span>
                  @if (item.ai_recommendation) {
                    <span class="ops-work-item__ai" [class]="aiRecClass(item.ai_recommendation)">
                      AI: {{ aiRecLabel(item.ai_recommendation) }}
                    </span>
                  }
                  <!-- ⑤ Overdue + stage-age -->
                  <span class="ops-work-item__sla">{{ overdueLabel(item.hours_overdue) }}</span>
                  @if (item.hours_in_stage > 0) {
                    <span class="ops-work-item__stage-age">
                      {{ hoursInStageLabel(item.hours_in_stage) }} in {{ item.review_stage_label | lowercase }}
                    </span>
                  }
                  <app-icon class="ops-work-item__chevron" name="arrow-right" [size]="16" />
                </a>
              </li>
            }
          </ul>
        }
      </div>
    </section>

    <!-- Recent submissions — secondary view (newest raw submissions) -->
    <section aria-label="Recent submissions">
      <div class="dash-panel">
        <div class="dash-panel__header">
          <div>
            <p class="dash-panel__title">Recent submissions</p>
            <p class="dash-panel__subtitle">Latest applications across both products</p>
          </div>
          <span class="dash-panel__icon"><app-icon name="document" [size]="18" /></span>
        </div>

        @if (recentApplications().length === 0) {
          <div class="dash-empty">
            <span class="dash-empty__icon"><app-icon name="document" [size]="32" /></span>
            <p class="dash-empty__title">No applications yet</p>
            <p class="dash-empty__body">Submitted applications will appear here.</p>
          </div>
        } @else {
          <div class="ops-recent-wrap">
            <table class="ops-recent">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Reference</th>
                  <th>Business</th>
                  <th>Applicant</th>
                  <th class="ops-recent__num">Amount</th>
                  <th>Stage</th>
                  <th>AI</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                @for (app of recentApplications(); track app.short_id) {
                  <tr
                    class="ops-recent__row"
                    [routerLink]="app.url"
                    [attr.aria-label]="'Open ' + app.product + ' application ' + app.short_id"
                  >
                    <td>
                      <span class="ops-work-item__product" [attr.data-product]="app.product">{{
                        app.product
                      }}</span>
                    </td>
                    <td class="ops-recent__ref">{{ app.short_id }}</td>
                    <td>{{ app.entity_name }}</td>
                    <td>{{ app.applicant | dash }}</td>
                    <td class="ops-recent__num">{{ app.amount | zar }}</td>
                    <td>{{ stageLabel(app.stage) }}</td>
                    <td>
                      @if (app.ai_recommendation) {
                        <span class="ops-work-item__ai" [class]="aiRecClass(app.ai_recommendation)">
                          {{ aiRecLabel(app.ai_recommendation) }}
                        </span>
                      } @else {
                        {{ app.ai_recommendation | dash }}
                      }
                    </td>
                    <td class="ops-recent__muted">{{ app.created_at | date: 'mediumDate' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </section>
  }
</div>
